import { RouteComponentProps } from '@reach/router';
import React from 'react';
import {
  AppBbWaardecheckAppraisalRequestSuccess,
  AppVboWaardecheckAppraisalRequestSuccess,
} from '~/graphql/types';
import InputGroupDivider from '~/scenes/Apps/components/InputGroupDivider';

import AppDetailsContainer from '../../../AppDetailsContainer';
import { useRecoilState } from 'recoil';
import {
  AppWaardecheckFieldsFragment,
  appWaardecheckState,
  StateId,
} from '../..';
import { mergeDeepRight } from 'ramda';
import { DeepPartial } from 'utility-types';
import AppSectionHeader from '~/scenes/Apps/components/AppSectionHeader';
import WaardecheckIntl from '../../intl';
import AppEntryItemHeader from '~/scenes/Apps/components/AppEntryItemHeader';
import { WaardecheckDefaultData } from '../../utils/getDefaultData';
import Description from '../../components/Description';
import IntlInputComponent from '../../components/IntlInputComponent';
import MainPreview from '../../components/MainPreview';
import ImageInput from '../../components/ImageInput';
import IntlLinkInputComponent from '../../components/IntlLinkInputComponent';
import { Page } from '../../components/Preview';
import TEST_ID from './index.testid';
import Explanation from '../../components/Explanation';

export type Props = RouteComponentProps & {
  dataTestId?: string;
  defaultValues: WaardecheckDefaultData;
  data: AppWaardecheckFieldsFragment;
  loading: boolean;
};

const AppraisalRequestSuccess: React.FC<Props> = ({
  data,
  defaultValues,
  loading,
}) => {
  const original = defaultValues.appraisalRequestSuccess;
  const [updated, setUpdatedValue] = useRecoilState(
    appWaardecheckState(StateId.updated),
  );

  const updateValue = (
    value: DeepPartial<
      | AppBbWaardecheckAppraisalRequestSuccess
      | AppVboWaardecheckAppraisalRequestSuccess
    >,
  ) => {
    setUpdatedValue(prev => {
      if (!prev) return prev;

      return mergeDeepRight(prev, {
        appraisalRequestSuccess: value,
      }) as any as typeof prev;
    });
  };

  if (updated == null) return null;

  return (
    <AppDetailsContainer
      header="Waardebepaling aangevraagd"
      loading={loading}
      dataTestId={TEST_ID.CONTAINER}
    >
      <Explanation>
        Op deze pagina wordt bevestigd dat het aanvragen van de waardebepaling
        is geslaagd. Personaliseer de achtergrondfoto en teksten.
      </Explanation>

      <MainPreview data={updated} page={Page['/appraisal-request-success']} />
      <AppEntryItemHeader>{WaardecheckIntl.mainImage.title}</AppEntryItemHeader>
      <Description>{WaardecheckIntl.mainImage.description}</Description>
      <ImageInput
        initialUrl={data.appraisalRequestSuccess.image?.url}
        filename={data.__typename + 'appraisalrequestsuccess'}
        s3Key={updated?.appraisalRequestSuccess.image?.s3key}
        onChange={image => updateValue({ image })}
        dataTestid={TEST_ID.IMAGE}
      />
      <InputGroupDivider large />
      <AppSectionHeader>{WaardecheckIntl.section.text}</AppSectionHeader>
      <Description>{WaardecheckIntl.section.textDescription}</Description>

      <IntlInputComponent
        title={WaardecheckIntl.heading}
        defaultValue={original.heading}
        value={updated.appraisalRequestSuccess.heading}
        onChange={heading => updateValue({ heading })}
        dataTestId={TEST_ID.HEADING}
      />
      <InputGroupDivider />
      <IntlInputComponent
        title="Toelichting"
        defaultValue={original.body}
        value={updated.appraisalRequestSuccess.body}
        onChange={body => updateValue({ body })}
        dataTestId={TEST_ID.BODY}
      />
      <InputGroupDivider />
      <IntlLinkInputComponent
        id="primaryButton"
        title="Call to action button"
        defaultValue={original.primaryButton}
        value={updated.appraisalRequestSuccess.primaryButton}
        onChange={primaryButton => updateValue({ primaryButton })}
        dataTestId={TEST_ID.PRIMARY_BUTTON}
      />
    </AppDetailsContainer>
  );
};

export default AppraisalRequestSuccess;
